import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useI18n } from '../../i18n/LanguageContext';
import { useMedia } from '../../site/MediaProvider';
import { Lang, LANGS } from '../../types';

const OG_LOCALE: Record<Lang, string> = { de: 'de_DE', en: 'en_US', it: 'it_IT' };

interface Props {
  /** override page title (else seo.title) */
  title?: string;
  /** override description (else seo.description) */
  description?: string;
  /** path of the current page for canonical, e.g. '/' or '/privacy-policy' */
  path?: string;
  /** emit hreflang alternates for de/en/it/x-default (landing only) */
  alternates?: boolean;
  /** emit Organization + LocalBusiness JSON-LD (landing only) */
  structuredData?: boolean;
}

function origin(): string {
  // Prefer the configured production site URL so canonical/hreflang/OG are correct
  // in the prerendered HTML (which is captured from a local server) and in prod.
  const configured = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');
  if (configured) return configured;
  if (typeof window !== 'undefined' && window.location) return window.location.origin;
  return 'https://www.rts-spa.com';
}

const Seo: React.FC<Props> = ({ title, description, path = '/', alternates, structuredData }) => {
  const { t, lang } = useI18n();
  const { single } = useMedia();
  const base = origin();

  const pageTitle = title || t('seo.title');
  const pageDesc = description || t('seo.description');
  const keywords = t('seo.keywords');
  const heroSlot = t('seo.ogImage') || 'hero';
  const ogImage = single(heroSlot) || `${base}/logo512.png`;
  const canonical = base + path;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'RTS S.p.A.',
      url: base,
      logo: single('logo') || `${base}/logo512.png`,
      email: 'info@rts-spa.com',
      vatID: 'IT14707871001',
      description: pageDesc,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via Delle Quattro Fontane 16',
        postalCode: '00184',
        addressLocality: 'Roma',
        addressCountry: 'IT',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'RTS S.p.A. – Roverbella Plant',
      telephone: '+39 0376 1410462',
      email: 'info@rts-spa.com',
      url: base,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via Paesa 40',
        postalCode: '46048',
        addressLocality: 'Roverbella',
        addressRegion: 'MN',
        addressCountry: 'IT',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 45.1936, longitude: 10.7739 },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'RTS S.p.A. – Sermoneta Plant',
      telephone: '+39 0773 1331088',
      email: 'info@rts-spa.com',
      url: base,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via della Meccanica 2',
        postalCode: '04013',
        addressLocality: 'Sermoneta',
        addressRegion: 'LT',
        addressCountry: 'IT',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 41.5519, longitude: 12.9871 },
    },
  ];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />

      {alternates && LANGS.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={`${base}/${l}`} />
      ))}
      {alternates && <link rel="alternate" hrefLang="x-default" href={`${base}/`} />}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="RTS S.p.A." />
      <meta property="og:locale" content={OG_LOCALE[lang]} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={ogImage} />

      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
